import React, { useState, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { useSelector } from "react-redux";

import CardSection from "../../../components/CardSection/CardSection";
import { selectActiveUid } from "../../Auth/authSlice";
import {
  getKinshasaDateTime,
  getKinshasaDate,
  getKinshasaTimeHMS,
} from "../../../utils/getKinshasaDateTime";
import { db } from "../../../config/firebaseConfig";
import { doc, setDoc, getDoc } from "firebase/firestore";
import MyFermeture from "../../../components/CardSection/MyFermeture";
import MyDatePicker from "../../../components/CardSection/MyDatePicker";
import { getKinshasaDateIso } from "../../../utils/dateUtils";

const Fermeture = () => {
  const activeUid = useSelector(selectActiveUid);

  const [sortieSu, setSortieSu] = useState("");
  const [sortieGo, setSortieGo] = useState("");
  const [nameValue, setNameValue] = useState("");
  const [dateFermeture, setDateFermeture] = useState(() =>
    getKinshasaDateIso(),
  );
  const [cardOpen, setCardOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verList, setVerList] = useState([]);
  const [verValues, setVerValues] = useState({});
  const [error, setError] = useState("");

  // Récupération du champ 'ver' Firestore
  useEffect(() => {
    const fetchVer = async () => {
      const uid = activeUid;
      if (!uid) return;
      try {
        const userDocRef = doc(db, "users", uid);
        const userDocSnap = await getDoc(userDocRef);
        const verString = userDocSnap.exists()
          ? userDocSnap.data().ver || ""
          : "";
        // Parsing selon guide
        const parseVerOrder = (verString) => {
          if (!verString) return [];
          const tokens = verString.split("_");
          const order = [];
          for (const token of tokens) {
            order.push(`${token}_CDF`);
            order.push(`${token}_USD`);
          }
          return order;
        };
        setVerList(parseVerOrder(verString));
      } catch (e) {
        console.error("Erreur chargement ver config:", e);
      }
    };
    fetchVer();
  }, [activeUid]);

  // Gestion dynamique des valeurs de versement
  const handleChangeVer = (key, value) => {
    setVerValues((prev) => ({ ...prev, [key]: value }));
  };

  const handleChangeSortieSu = (values) => {
    setSortieSu(values.value);
  };
  const handleChangeSortieGo = (values) => {
    setSortieGo(values.value);
  };
  const handleChangeText = (e) => {
    setNameValue(e?.target?.value ?? "");
  };
  const handleDateChange = (e) => {
    setDateFermeture(e.target.value);
    setError("");
  };

  const handleSend = async () => {
    // Validation: tous les champs de verList doivent être remplis
    if (
      !nameValue ||
      !sortieSu ||
      !sortieGo ||
      verList.some((k) => !verValues[k])
    )
      return;
    setLoading(true);
    setError("");
    // Use selected date as document ID in dd-mm-yyyy format
    const [year, month, day] = dateFermeture.split("-");
    const dateDocId = `${day}-${month}-${year}`;
    const uid = activeUid;
    if (!uid) {
      setLoading(false);
      return console.error("No authenticated user");
    }
    const stamp = getKinshasaDateTime();
    const timeKey = getKinshasaTimeHMS();

    try {
      const docRef = doc(db, "users", uid, "data", dateDocId);

      const snap = await getDoc(docRef);
      const currentData = snap.exists() ? snap.data() : {};

      // A closure was already declared for this exact date — block a second
      // one instead of silently adding another FERM_ entry. Corrections go
      // through Admin Correction.
      const alreadyClosed = Object.values(currentData).some(
        (v) => v?.type === "FERMETURE",
      );
      if (alreadyClosed) {
        setError(
          `Une fermeture existe déjà pour le ${dateDocId}. Utilisez la correction admin pour la modifier.`,
        );
        setLoading(false);
        return;
      }

      // Auto-complete missing Arrivée: if a pump has _D but no _A, set _A = _D (pump did not work)
      const autoArrivee = {};
      for (const [key, val] of Object.entries(currentData)) {
        if (key.endsWith("_D") && val?.type === "INDEX") {
          const pump = key.slice(0, -2);
          if (!currentData[`${pump}_A`]) {
            autoArrivee[`${pump}_A`] = { ...val, date: stamp };
          }
        }
      }

      await setDoc(
        docRef,
        {
          ...autoArrivee,
          [`FERM_${timeKey}`]: {
            sortie_su: sortieSu,
            sortie_go: sortieGo,
            name: nameValue,
            type: "FERMETURE",
            date: stamp,
            dateFermeture: dateFermeture,
            ...verList.reduce((acc, key) => {
              acc[key.toLowerCase()] = verValues[key];
              return acc;
            }, {}),
          },
        },
        { merge: true },
      );

      setSortieSu("");
      setSortieGo("");
      setNameValue("");
      setVerValues({});
      setDateFermeture(() => getKinshasaDateIso());
      setCardOpen(false); // Close after Firestore write
    } catch (e) {
      console.error("Error sending: ", e);
    }
    setLoading(false);
  };

  return loading ? (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginTop: 32,
      }}
    >
      <CircularProgress color="primary" size={48} />
      <div style={{ marginTop: 16, fontSize: 18 }}>Chargement...</div>
    </div>
  ) : (
    <CardSection
      title="-- FERMETURE --"
      showDefaultControls={false}
      open={cardOpen}
      setOpen={setCardOpen}
    >
      <MyFermeture
        sortie_su={sortieSu}
        onSortieSuChange={handleChangeSortieSu}
        sortie_go={sortieGo}
        onSortieGoChange={handleChangeSortieGo}
        textValue={nameValue}
        onTextChange={handleChangeText}
        dateValue={dateFermeture}
        onDateChange={handleDateChange}
        verList={verList}
        verValues={verValues}
        onVerChange={handleChangeVer}
        onSend={handleSend}
        error={error}
      />
    </CardSection>
  );
};

export default Fermeture;
